/**
 * Passthrough components that switch between @material-ui (UX2) and @mui/material (UX3)
 * based on `mui_compat` in ComponentVersionsContext.
 */
/* eslint-disable @typescript-eslint/no-explicit-any */
import type { ComponentProps } from 'react'
import React, { forwardRef } from 'react'
import {
  AppBar as M4AppBar,
  Avatar as M4Avatar,
  Breadcrumbs as M4Breadcrumbs,
  Box as M4Box,
  CardActions as M4CardActions,
  Divider as M4Divider,
  LinearProgress as M4LinearProgress,
  List as M4List,
  ListItem as M4ListItem,
  ListItemText as M4ListItemText,
  ListSubheader as M4ListSubheader,
  Paper as M4Paper,
  Popper as M4Popper,
  Slider as M4Slider,
  Step as M4Step,
  StepButton as M4StepButton,
  StepConnector as M4StepConnector,
  StepContent as M4StepContent,
  StepIcon as M4StepIcon,
  StepLabel as M4StepLabel,
  TableContainer as M4TableContainer,
  TableFooter as M4TableFooter,
  Toolbar as M4Toolbar,
} from '@material-ui/core'
import { default as M4Stepper } from '@material-ui/core/Stepper'
import { AlertTitle as M4AlertTitle, AvatarGroup as M4AvatarGroup } from '@material-ui/lab'
import {
  AppBar as M5AppBar,
  AlertTitle as M5AlertTitle,
  Avatar as M5Avatar,
  AvatarGroup as M5AvatarGroup,
  Breadcrumbs as M5Breadcrumbs,
  CardActions as M5CardActions,
  LinearProgress as M5LinearProgress,
  List as M5List,
  ListItem as M5ListItem,
  ListItemText as M5ListItemText,
  ListSubheader as M5ListSubheader,
  Divider as M5Divider,
  Paper as M5Paper,
  Popper as M5Popper,
  Slider as M5Slider,
  Stack as M5Stack,
  Step as M5Step,
  StepButton as M5StepButton,
  StepConnector as M5StepConnector,
  StepContent as M5StepContent,
  StepIcon as M5StepIcon,
  StepLabel as M5StepLabel,
  TableContainer as M5TableContainer,
  TableFooter as M5TableFooter,
  Toolbar as M5Toolbar,
} from '@mui/material'
import { default as M5Stepper } from '@mui/material/Stepper'
import { OsMuiCompatVersion } from '../context/muiCompatVersion'
import { useComponentVersion } from '../hooks'

function useMuiCompatVersion(): OsMuiCompatVersion {
  const v = useComponentVersion('mui_compat')
  return v === OsMuiCompatVersion.V2 ? OsMuiCompatVersion.V2 : OsMuiCompatVersion.V1
}

function dual(M4: React.ComponentType<any>, M5: React.ComponentType<any>, displayName: string) {
  const C = forwardRef<any, any>((props, ref) => {
    const v = useMuiCompatVersion()
    return v === OsMuiCompatVersion.V2 ? <M5 {...props} ref={ref} /> : <M4 {...props} ref={ref} />
  })
  C.displayName = displayName
  return C
}

export const AppBar = dual(M4AppBar, M5AppBar, 'AppBar')
export const AlertTitle = dual(M4AlertTitle, M5AlertTitle, 'AlertTitle')
export const Avatar = dual(M4Avatar, M5Avatar, 'Avatar')
export const AvatarGroup = dual(M4AvatarGroup, M5AvatarGroup, 'AvatarGroup')
export const Breadcrumbs = dual(M4Breadcrumbs, M5Breadcrumbs, 'Breadcrumbs')
export const CardActions = dual(M4CardActions, M5CardActions, 'CardActions')
export const LinearProgress = dual(M4LinearProgress, M5LinearProgress, 'LinearProgress')
export const List = dual(M4List, M5List, 'List')
export const ListItem = dual(M4ListItem, M5ListItem, 'ListItem')
export const ListItemText = dual(M4ListItemText, M5ListItemText, 'ListItemText')
export const ListSubheader = dual(M4ListSubheader, M5ListSubheader, 'ListSubheader')
export const MuiDivider = dual(M4Divider, M5Divider, 'MuiDivider')
export const Paper = dual(M4Paper, M5Paper, 'Paper')
export const Popper = dual(M4Popper, M5Popper, 'Popper')
export const Slider = dual(M4Slider, M5Slider, 'Slider')

/** MUI5 Stack; under MUI4 uses flex Box (approximate for direction + gap/spacing). */
export const Stack = forwardRef<any, ComponentProps<typeof M5Stack>>((props, ref) => {
  const v = useMuiCompatVersion()
  if (v === OsMuiCompatVersion.V2) {
    return <M5Stack ref={ref} {...props} />
  }
  const { direction = 'column', spacing, gap, sx, style, ...rest } = props
  const flexDirection = direction === 'row' || direction === 'row-reverse' ? direction : 'column'
  const themeSpacing = typeof spacing === 'number' ? spacing : 0
  const gapFromSpacing = themeSpacing ? `${Number(themeSpacing) * 8}px` : undefined
  const gapPx = typeof gap === 'string' || typeof gap === 'number' ? gap : gapFromSpacing
  return (
    <M4Box
      {...(rest as any)}
      ref={ref}
      display="flex"
      flexDirection={flexDirection}
      style={{ ...(gapPx != null ? { gap: gapPx } : {}), ...style }}
    />
  )
})
Stack.displayName = 'Stack'

export const Step = dual(M4Step, M5Step, 'Step')
export const StepButton = dual(M4StepButton, M5StepButton, 'StepButton')
export const StepConnector = dual(M4StepConnector, M5StepConnector, 'StepConnector')
export const StepContent = dual(M4StepContent, M5StepContent, 'StepContent')
export const StepIcon = dual(M4StepIcon, M5StepIcon, 'StepIcon')
export const StepLabel = dual(M4StepLabel, M5StepLabel, 'StepLabel')
export const Stepper5 = dual(M4Stepper, M5Stepper, 'Stepper5')
export const TableContainer = dual(M4TableContainer, M5TableContainer, 'TableContainer')
export const TableFooter = dual(M4TableFooter, M5TableFooter, 'TableFooter')
export const Toolbar = dual(M4Toolbar, M5Toolbar, 'Toolbar')

export type { ButtonProps as MuiButtonProps } from '@mui/material'
export type { StepIconProps } from '@mui/material/StepIcon'
